import { CORS_POLICY } from '@/shared/config';
import type { CropBox } from '@/shared/config/types';
import {
  fetchWithStorageRetry,
  getImageSrc,
  getRotationCoverScale,
} from '@/shared/lib';

type PhotoTransform = {
  rotation: number;
  flipHorizontal: boolean;
  cropBox: CropBox | null;
  zoom: number;
  panX: number;
  panY: number;
};

/**
 * Resolves an API image URL to a direct presigned CDN URL
 * using the `returnURL=true` query parameter.
 */
const resolveImageUrl = async (url: string): Promise<string> => {
  const separator = url.includes('?') ? '&' : '?';
  const response = await fetchWithStorageRetry(
    `${url}${separator}returnURL=true`,
    {
      credentials: CORS_POLICY,
      redirect: 'manual',
    },
  );

  return response.text();
};

/**
 * Fetches an image via its presigned CDN URL and loads it into
 * an HTMLImageElement suitable for canvas rendering (no CORS taint).
 */
const loadImage = async (url: string): Promise<HTMLImageElement> => {
  const imageUrl = await resolveImageUrl(url);
  const response = await fetchWithStorageRetry(imageUrl);

  if (!response.ok) {
    throw new Error(`Failed to load image: ${response.status}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to decode image'));
    };

    img.src = objectUrl;
  });
};

const isTransformed = (transform: PhotoTransform): boolean =>
  transform.rotation !== 0 ||
  transform.flipHorizontal ||
  transform.cropBox !== null ||
  transform.zoom !== 1 ||
  transform.panX !== 0 ||
  transform.panY !== 0;

/**
 * Downloads a dental photo with all transforms (rotation, flip, crop, zoom, pan)
 * baked into the image. Uses JPEG encoding for reasonable file sizes.
 *
 * If no transforms are applied, downloads the original file directly.
 */
export const downloadTransformedPhoto = async (
  assetID: string,
  fileName: string,
  transform: PhotoTransform,
): Promise<void> => {
  const url = getImageSrc(assetID, 'original');

  if (!isTransformed(transform)) {
    const imageUrl = await resolveImageUrl(url);
    const response = await fetchWithStorageRetry(imageUrl);

    if (!response.ok) {
      return;
    }

    const blob = await response.blob();
    triggerDownload(blob, `${fileName}.jpg`);

    return;
  }

  const img = await loadImage(url);
  const blob = await renderTransformedImage(img, transform);

  triggerDownload(blob, `${fileName}.jpg`);
};

// Safe canvas pixel limit for mobile browsers (iOS Safari ~16.7MP)
const MAX_CANVAS_PIXELS = 16_000_000;

const clampCanvasSize = (
  width: number,
  height: number,
): { width: number; height: number } => {
  const pixels = width * height;

  if (pixels <= MAX_CANVAS_PIXELS) {
    return { width, height };
  }

  const scale = Math.sqrt(MAX_CANVAS_PIXELS / pixels);

  return {
    width: Math.floor(width * scale),
    height: Math.floor(height * scale),
  };
};

const renderTransformedImage = async (
  img: HTMLImageElement,
  transform: PhotoTransform,
): Promise<Blob> => {
  const { rotation, flipHorizontal, cropBox, zoom, panX, panY } = transform;

  const radians = (rotation * Math.PI) / 180;
  const naturalW = img.naturalWidth;
  const naturalH = img.naturalHeight;

  if (cropBox) {
    return renderCropped(
      img,
      naturalW,
      naturalH,
      cropBox,
      radians,
      flipHorizontal,
    );
  }

  return renderFullImage(
    img,
    naturalW,
    naturalH,
    rotation,
    radians,
    flipHorizontal,
    zoom,
    panX,
    panY,
  );
};

const renderCropped = (
  img: HTMLImageElement,
  naturalW: number,
  naturalH: number,
  cropBox: CropBox,
  radians: number,
  flipHorizontal: boolean,
): Promise<Blob> => {
  const cropPixelX = cropBox.x * naturalW;
  const cropPixelY = cropBox.y * naturalH;
  const cropPixelW = cropBox.width * naturalW;
  const cropPixelH = cropBox.height * naturalH;

  const clamped = clampCanvasSize(
    Math.round(cropPixelW),
    Math.round(cropPixelH),
  );
  const scaleX = clamped.width / cropPixelW;
  const scaleY = clamped.height / cropPixelH;

  const canvas = document.createElement('canvas');
  canvas.width = clamped.width;
  canvas.height = clamped.height;

  const ctx = canvas.getContext('2d')!;

  // Move origin to crop center, apply transforms, then draw
  ctx.translate(canvas.width / 2, canvas.height / 2);

  if (radians !== 0) {
    ctx.rotate(radians);
  }

  if (flipHorizontal) {
    ctx.scale(-1, 1);
  }

  // Draw image so that the crop center aligns with the canvas center
  const cropCenterX = cropPixelX + cropPixelW / 2;
  const cropCenterY = cropPixelY + cropPixelH / 2;

  ctx.drawImage(
    img,
    -cropCenterX * scaleX,
    -cropCenterY * scaleY,
    naturalW * scaleX,
    naturalH * scaleY,
  );

  return canvasToBlob(canvas);
};

const renderFullImage = (
  img: HTMLImageElement,
  naturalW: number,
  naturalH: number,
  rotationDeg: number,
  radians: number,
  flipHorizontal: boolean,
  zoom: number,
  panX: number,
  panY: number,
): Promise<Blob> => {
  // Check if rotation is near an exact 90° multiple
  const nearest90 = Math.round(rotationDeg / 90) * 90;
  const nearestMod = ((nearest90 % 360) + 360) % 360;
  const isExact90 = Math.abs(rotationDeg - nearest90) < 0.01;
  const swapDimensions = isExact90 && (nearestMod === 90 || nearestMod === 270);

  // Output canvas matches the original image rectangle (or swapped for 90/270).
  // Combined with coverScale this eliminates black corners from rotation.
  const rawW = swapDimensions ? naturalH : naturalW;
  const rawH = swapDimensions ? naturalW : naturalH;
  const clamped = clampCanvasSize(rawW, rawH);
  const downscale = clamped.width / rawW;

  const canvas = document.createElement('canvas');
  canvas.width = clamped.width;
  canvas.height = clamped.height;

  const ctx = canvas.getContext('2d')!;

  ctx.translate(clamped.width / 2, clamped.height / 2);

  if (radians !== 0) {
    ctx.rotate(radians);
  }

  if (flipHorizontal) {
    ctx.scale(-1, 1);
  }

  // Apply cover scale to fill the rectangle without black corners,
  // matching the UI behavior in PhotoSlider
  const coverScale = getRotationCoverScale(rotationDeg, naturalW / naturalH);
  const totalZoom = zoom * coverScale;

  if (totalZoom !== 1) {
    ctx.scale(totalZoom, totalZoom);
  }

  ctx.translate(panX * naturalW * downscale, panY * naturalH * downscale);

  ctx.drawImage(
    img,
    (-naturalW / 2) * downscale,
    (-naturalH / 2) * downscale,
    naturalW * downscale,
    naturalH * downscale,
  );

  return canvasToBlob(canvas);
};

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      'image/jpeg',
      0.92,
    );
  });

const triggerDownload = (blob: Blob, fileName: string): void => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => {
    window.URL.revokeObjectURL(link.href);
  }, 100);
};
